/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import Login from "./features/auth/pages/Login";
import AuthCallback from "./features/auth/pages/AuthCallback";
import { DeliveryProvider } from "./shared/context/DeliveryContext";
import { UserProvider } from "./shared/context/UserContext";
import { AuthProvider, useAuth } from "./shared/context/AuthContext";
import { TemplatesCatalogProvider } from "./features/templates/context/TemplatesCatalogContext";
import { UserPrefsProvider } from "./shared/context/UserPrefsContext";
import DataContextProvider from "./features/map-editor/context/DataContext";
import FocusContextProvider from "./features/map-editor/context/FocusContext";
import { ProjectUtilityProvider } from "./shared/context/item/ItemUtilityContext";
import { ProjectTagProvider } from "./features/projects/context/ProjectTagContext";
import { ProjectFilterProvider } from "./features/projects/context/ProjectFilterContext";
import { ProjectDataProvider } from "./features/projects/context/ProjectDataContext";
import { FilterPresetsBootstrapProvider } from "./shared/context/FilterPresetsBootstrapContext";
import { BreadcrumbProvider } from "./shared/context/BreadcrumbContext";
import { NavigationGuardProvider } from "./shared/context/NavigationGuardContext";
import { ToastProvider } from "./shared/context/ToastContext";
import LoginModal from "./features/auth/pages/LoginModal";
import LogoutOverlay from "./shared/components/layout/LogoutOverlay";
import LoadingIndicator from "./shared/components/ui/LoadingIndicator";
import { setupAxiosInterceptors, setAuthContext } from "./services/axiosConfig";
import { setPostLoginContext } from "./features/share-links/utils/shareAccessSession";

import "./styles/main.css";
import { getBearerToken } from "./services/authToken";

const Dashboard = lazy(() => import("./features/dashboard/pages/Dashboard"));
const WorkFlows = lazy(() => import("./features/dashboard/pages/WorkFlows"));
const DeliveryList = lazy(() => import("./features/deliveries/pages/DeliveryList"));
const UserRegistration = lazy(() => import("./features/users/pages/UserRegistration"));
const MapView = lazy(() => import("./features/deliveries/pages/MapView"));
const TaskReport = lazy(() => import("./features/deliveries/pages/TaskReport"));
const LiveReport = lazy(() => import("./features/deliveries/pages/LiveReport"));
const LocalityStatsTable = lazy(
  () => import("./features/deliveries/pages/LocalityStatsTable"),
);
const MapEditor = lazy(() => import("./features/map-editor/pages/MapEditor"));
const ProjectAdministration = lazy(
  () => import("./features/projects/pages/ProjectAdministration"),
);
const Collections = lazy(() => import("./features/collections/pages/Collections"));
const RecordResolverPage = lazy(
  () => import("./features/share-links/pages/RecordResolverPage"),
);
const PublicProjectViewPage = lazy(
  () => import("./features/share-links/pages/PublicProjectViewPage"),
);
const InvalidShareLinkPage = lazy(
  () => import("./features/share-links/pages/InvalidShareLinkPage"),
);
const PublicMapEditorPage = lazy(
  () => import("./features/share-links/pages/PublicMapEditorPage"),
);
const NotPublicSharePage = lazy(
  () => import("./features/share-links/pages/NotPublicSharePage"),
);
const ShareOpenPage = lazy(() => import("./features/share-links/pages/ShareOpenPage"));

// Setup Axios interceptors
setupAxiosInterceptors();

const RouteLoadingFallback = () => (
  <LoadingIndicator fullPage text="Loading..." />
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const location = useLocation();
  const isAuthenticated = !!getBearerToken();
  // const userRoles = JSON.parse(sessionStorage.getItem('roles') || '[]');
  // const hasRequiredRole = allowedRoles.some(role =>
  //     userRoles.some(userRole => userRole.name === role)
  // );

  if (!isAuthenticated) {
    const shareOpenMatch = location.pathname.match(/^\/share\/open\/([^/]+)/);
    if (shareOpenMatch?.[1]) {
      const recordId = decodeURIComponent(shareOpenMatch[1]);
      setPostLoginContext({
        reason: "share_open",
        resourceType: recordId.startsWith("CD_") ? "collection" : "project",
        resourceId: recordId,
        postLoginRedirectPath: `/share/open/${encodeURIComponent(recordId)}`,
      });
      return <Navigate to="/login?reason=share_open" replace />;
    }
    return <Navigate to="/login" />;
  }

  // if (!hasRequiredRole) {
  //     return <Navigate to="/delivery-list" />;
  // }

  return children;
};

// Component to initialize the auth context reference
const AuthContextInitializer = () => {
  const authContext = useAuth();

  useEffect(() => {
    setAuthContext(authContext);
  }, [authContext]);

  return null;
};

const App = () => {
  const allAppRoles = [
    "ROLE_USER",
    "ROLE_SUPERVISOR",
    "ROLE_ADMIN",
    "ROLE_MANAGER",
  ];

  const ProjectAreaLayout = () => (
    <ProtectedRoute allowedRoles={allAppRoles}>
      <FocusContextProvider>
        <DataContextProvider>
          <ProjectUtilityProvider>
            <ProjectTagProvider>
              <ProjectFilterProvider>
                <FilterPresetsBootstrapProvider>
                  <ProjectDataProvider>
                    <Outlet />
                  </ProjectDataProvider>
                </FilterPresetsBootstrapProvider>
              </ProjectFilterProvider>
            </ProjectTagProvider>
          </ProjectUtilityProvider>
        </DataContextProvider>
      </FocusContextProvider>
    </ProtectedRoute>
  );

  const PublicProjectViewerLayout = () => (
    <ProjectUtilityProvider>
      <ProjectTagProvider>
        <Outlet />
      </ProjectTagProvider>
    </ProjectUtilityProvider>
  );

  return (
    <Router>
      <NavigationGuardProvider>
      <AuthProvider>
        <TemplatesCatalogProvider>
        <ToastProvider>
        <UserPrefsProvider>
          <AuthContextInitializer />
          <BreadcrumbProvider>
            <UserProvider>
              <Suspense fallback={<RouteLoadingFallback />}>
              <Routes>
              <Route path="/login" element={<Login />} />
              {/* Single-Sign-On Callback */}
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route
                path="/record/:recordId"
                element={<RecordResolverPage />}
              />
              <Route
                path="/record-invalid"
                element={<InvalidShareLinkPage />}
              />
              <Route
                path="/record-not-public/:recordId"
                element={<NotPublicSharePage />}
              />
              <Route
                path="/record-not-public"
                element={<NotPublicSharePage />}
              />
              <Route
                path="/share/open/:recordId"
                element={
                  <ProtectedRoute allowedRoles={allAppRoles}>
                    <ShareOpenPage />
                  </ProtectedRoute>
                }
              />
              <Route element={<PublicProjectViewerLayout />}>
                <Route
                  path="/public/project/:recordId"
                  element={<PublicProjectViewPage />}
                />
                <Route
                  path="/public/collection/:recordId"
                  element={<PublicMapEditorPage />}
                />
                <Route
                  path="/public/map-editor/:recordId"
                  element={<PublicMapEditorPage />}
                />
              </Route>

              <Route
                path="/"
                element={
                  <ProtectedRoute allowedRoles={allAppRoles}>
                    <DeliveryProvider>
                      <Dashboard />
                    </DeliveryProvider>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute allowedRoles={allAppRoles}>
                    <DeliveryProvider>
                      <Dashboard />
                    </DeliveryProvider>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/work-flows"
                element={
                  <ProtectedRoute allowedRoles={allAppRoles}>
                    <DeliveryProvider>
                      <WorkFlows />
                    </DeliveryProvider>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/delivery-list"
                element={
                  <ProtectedRoute allowedRoles={allAppRoles}>
                    <DeliveryProvider>
                      <DeliveryList />
                    </DeliveryProvider>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/map"
                element={
                  <ProtectedRoute allowedRoles={allAppRoles}>
                    <DeliveryProvider>
                      <MapView />
                    </DeliveryProvider>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/register-user"
                element={
                  <ProtectedRoute allowedRoles={allAppRoles}>
                    <UserRegistration />
                  </ProtectedRoute>
                }
              />

              <Route path="/settings" element={<Navigate to="/dashboard" replace />} />

              <Route
                path="/task-report"
                element={
                  <ProtectedRoute allowedRoles={allAppRoles}>
                    <DeliveryProvider>
                      <TaskReport />
                    </DeliveryProvider>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/live-report"
                element={
                  <ProtectedRoute allowedRoles={allAppRoles}>
                    <DeliveryProvider>
                      <LiveReport />
                    </DeliveryProvider>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/locality-stats"
                element={
                  <ProtectedRoute allowedRoles={allAppRoles}>
                    <DeliveryProvider>
                      <LocalityStatsTable />
                    </DeliveryProvider>
                  </ProtectedRoute>
                }
              />

              <Route element={<ProjectAreaLayout />}>
                <Route
                  path="/project-administration"
                  element={<ProjectAdministration />}
                />
              </Route>

              <Route
                path="/collections"
                element={
                  <ProtectedRoute allowedRoles={allAppRoles}>
                    <FocusContextProvider>
                      <DataContextProvider>
                        <ProjectUtilityProvider>
                          <ProjectTagProvider>
                            <ProjectFilterProvider>
                              <Collections />
                            </ProjectFilterProvider>
                          </ProjectTagProvider>
                        </ProjectUtilityProvider>
                      </DataContextProvider>
                    </FocusContextProvider>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/map-editor"
                element={
                  <ProtectedRoute allowedRoles={allAppRoles}>
                    <FocusContextProvider>
                      <DataContextProvider>
                        <ProjectUtilityProvider>
                          <ProjectTagProvider>
                            <ProjectFilterProvider>
                              <MapEditor />
                            </ProjectFilterProvider>
                          </ProjectTagProvider>
                        </ProjectUtilityProvider>
                      </DataContextProvider>
                    </FocusContextProvider>
                  </ProtectedRoute>
                }
              />
              </Routes>
              </Suspense>
              <LoginModal />
              <LogoutOverlay />
            </UserProvider>
          </BreadcrumbProvider>
        </UserPrefsProvider>
        </ToastProvider>
        </TemplatesCatalogProvider>
      </AuthProvider>
      </NavigationGuardProvider>
    </Router>
  );
};

export default App;
